// Shared SF2 reverb bus, as an AudioWorklet.
//
// Follows db-worklet.civet's pattern — stringify a processor, hand it to `addModule` as a
// blob URL, register it once per context through `prepareContext` — because that is what
// makes the same node work in the live context and in the OfflineAudioContext the exporter
// renders into.
//
// The processor is a thin wrapper: everything that decides what the reverb sounds like is
// `createReverb` in reverb.civet, stringified into the module below so the worklet runs the
// exact function the tests run.

{ createReverb, REVERB_RT60, REVERB_RT60_MAX } from ./reverb.civet

export { REVERB_RT60, REVERB_DRAIN_SECONDS } from ./reverb.civet

src := ```
const createReverb = ${createReverb.toString()};

class SF2Reverb extends AudioWorkletProcessor {
  // GS reverb-time SysEx arrives as an AudioParam rather than a port message (see
  // ChannelSynth.fxParam). A message is delivered whenever the main thread gets to it,
  // which is not a fixed point on the render clock — so the quantum a SysEx retune landed
  // on varied between renders of the same song, and two exports of one file were not
  // sample-identical. Param automation is scheduled against the same clock the graph is
  // rendered on, so it lands on the same quantum every time.
  //
  // k-rate: one value per quantum. `setRT60` re-solves a feedback coefficient per comb,
  // which is far too much to run per sample, and reverb time is a knob that moves a
  // handful of times in a song rather than continuously.
  static get parameterDescriptors() {
    return [{
      name: "rt60",
      defaultValue: ${REVERB_RT60},
      minValue: 0.1,
      maxValue: ${REVERB_RT60_MAX},
      automationRate: "k-rate",
    }];
  }

  constructor(options) {
    super(options);
    this.reverb = createReverb(sampleRate, ${REVERB_RT60});
    // Last value applied, so an unchanged param costs a compare rather than a re-solve.
    this.rt60 = ${REVERB_RT60};
  }

  process (inputs, outputs, parameters) {
    // k-rate, so the array holds exactly one value for the whole quantum.
    const rt60 = parameters.rt60[0];
    if (rt60 !== this.rt60) {
      this.rt60 = rt60;
      this.reverb.setRT60(rt60);
    }

    const output = outputs[0];
    const input = inputs[0];

    if (!output || !output.length) {
      return true;
    }

    const outL = output[0];
    const outR = output[1] || output[0];
    const inL = input && input[0];
    const inR = (input && input[1]) || inL;
    const reverb = this.reverb;
    const n = outL.length;

    for (let i = 0; i < n; i++) {
      reverb.process(inL ? inL[i] : 0, inR ? inR[i] : 0);
      outL[i] = reverb.outL;
      if (outR !== outL) {
        outR[i] = reverb.outR;
      }
    }

    // Never return false: the bus outlives every voice that feeds it, and a tail is still
    // ringing for `REVERB_DRAIN_SECONDS` after its input goes silent.
    //
    // POSSIBLE OPTIMIZATION: the FDN runs on every quantum even when nothing has ever fed
    // it, so a fully dry song pays for the reverb continuously. A silence gate — skip the
    // loop and write zeros once the input has been silent past the drain — would make
    // unused reverb nearly free while keeping the tail semantics.
    return true;
  }
}

registerProcessor('sf2Reverb', SF2Reverb)
```

/** The generated module, exported so a test can at least parse it outside a worklet. */
export processorSource := src

blob := new Blob [src], type: "application/javascript"
url := URL.createObjectURL(blob)

export ReverbWorklet :=
  /**
  Create the shared reverb bus node. `prepareContext` must have resolved for this context
  first, exactly as with `DBWorklet.createDBGainNode`.
  */
  createReverbNode: (context: BaseAudioContext) ->
    new AudioWorkletNode context, 'sf2Reverb',
      numberOfInputs: 1
      numberOfOutputs: 1
      outputChannelCount: [2]
      channelCount: 2
      channelCountMode: "explicit"
      channelInterpretation: "speakers"

  /**
  Register the processor on a context. Cached per context, and kept in its own field rather
  than sharing `DBWorklet`'s so that a context which only wants one of the two doesn't pay
  for both.
  */
  prepareContext: (context: BaseAudioContext & {reverbPrepared?: Promise<void>}) ->
    return context.reverbPrepared if context.reverbPrepared

    if !context.audioWorklet?.addModule
      // Safari / older browsers: no worklets. Resolve anyway — `createReverbNode` then
      // throws on the missing constructor and the caller falls back to a dry bus, which is
      // the same failure path as a module that fails to load.
      return context.reverbPrepared = Promise.resolve()

    context.reverbPrepared = context.audioWorklet.addModule(url)
    .catch (e) ->
      context.reverbPrepared = undefined
      throw e

export default ReverbWorklet
